/**
 * THE SHELF — what is standing on it, against what came in.
 *
 * ── THE TWO NUMBERS ARE COUNTED DIFFERENTLY, AND THE PANEL SAYS SO ──
 *
 * `packages_pending` is an AS-OF figure: what is on the shelf at the end of the
 * period, whenever it arrived — including a parcel logged four months ago that
 * nobody has collected. `packages_logged` is a WINDOW figure: what came in
 * during the period.
 *
 * They are therefore NOT a part and its whole in the arithmetic sense, and the
 * ratio between them is not a collection rate. What it IS is a useful shape:
 * a shelf holding more than a whole period's intake is a shelf with a backlog,
 * and that is visible at a glance from across a desk.
 *
 * Saying that out loud is the entire reason this panel is allowed to draw a
 * ring. A ring implies a part of a whole; these two are not, so the caption
 * carries the distinction and the tooltip carries it again.
 *
 * ── AND IT DOES NOT SAY "COLLECTION RATE" ──
 *
 * The tempting figure — collected over logged — needs `packages_picked_up`,
 * which the read model does have but which the HEADLINE keys do not include, so
 * it is not on this response. Inventing it from the two figures that ARE here
 * would be a rate computed from a window count and an as-of count: two clocks,
 * one fraction.
 */
import React from 'react';
import { AnalyticsOverview } from '../../../../../api/analytics-client';
import { dataViz, palette } from '../../../../../style-utils/theme';
import { PanelShell, ChartShellState } from '../charts/chart-shell';
import { RingChart } from '../charts/ring-chart';
import { RingRow } from './panels.styled';

const PANEL_ID = 'shelf';

export interface ShelfPanelProps {
  state: ChartShellState;
  overview?: AnalyticsOverview;
  /** The dwell figures, for the second ring. Absent while it loads. */
  breached?: number | null;
  breachedOf?: number | null;
  thresholdHours?: number | null;
  onExportCsv?: () => void;
}

export const ShelfPanel: React.FC<ShelfPanelProps> = ({
  state,
  overview,
  breached,
  breachedOf,
  thresholdHours,
  onExportCsv,
}) => {
  const standing = overview?.kpis?.packages_pending?.value ?? null;
  const logged = overview?.kpis?.packages_logged?.value ?? null;

  return (
    <PanelShell
      id={PANEL_ID}
      title="The shelf, and what missed the threshold"
      state={state}
      onExportCsv={onExportCsv}
      caveat="Two different clocks, side by side. The shelf is counted AS OF NOW — a parcel logged in March that nobody has collected is on it. The intake is counted INSIDE the period. So the left-hand figure is not a fraction of the right-hand one; what it shows is whether the shelf is holding more than a period's worth of arrivals, which is what a backlog looks like."
      emptyReason={
        overview && standing == null && logged == null
          ? {
              title: 'No parcel figures in this scope',
              text: 'Nothing you can see logs parcels, so there is no shelf to measure.',
            }
          : null
      }
    >
      <RingRow>
        <RingChart
          testId="shelf-ring"
          label="On the shelf now, against this period's intake"
          value={standing ?? 0}
          total={logged}
          centre={standing == null ? undefined : standing.toLocaleString()}
          tone={dataViz.series[0]}
          note={
            standing == null || logged == null ? null : (
              <>
                <strong>{standing.toLocaleString()}</strong> standing now ·{' '}
                {logged.toLocaleString()} logged this period
              </>
            )
          }
        />

        {/* THE BREACH RING IS A REAL PART OF A REAL WHOLE — how many of the
            parcels that were handed over in this period took longer than the
            threshold — so it is the one of the two that a percentage genuinely
            describes. */}
        <RingChart
          testId="breach-ring"
          label={`Handovers past the ${thresholdHours ?? 48}-hour threshold`}
          value={breached ?? 0}
          total={breachedOf ?? null}
          tone={palette.warning}
          note={
            breached == null || !breachedOf ? null : (
              <>
                <strong>{breached.toLocaleString()}</strong> of{' '}
                {breachedOf.toLocaleString()} took longer than {thresholdHours ?? 48}h
              </>
            )
          }
        />
      </RingRow>
    </PanelShell>
  );
};
